import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Field, Heading, Input, Label, Paragraph, Select } from '@digdir/designsystemet-react';
import { Routes } from '../../../enums/Routes';
import { buildRoute } from '../../../utils/routeBuilder';
import { categoriesOf, historyByMonth, TemplateItem } from '../customSurveysView';
import classes from '../CustomSurveyDashboardPage.module.css';

const locale = (language: string) => (language === 'en' ? 'en-GB' : 'nb-NO');

const HistoryRow = ({ item }: { item: TemplateItem }) => {
  const { t, i18n } = useTranslation();
  const { template, latest } = item;
  const date = latest?.date ? new Date(latest.date) : undefined;
  const percent = latest ? Math.round(latest.responseRate) : 0;

  return (
    <li className={classes.historyRow}>
      <span aria-hidden="true" className={classes.dateBadge}>
        {date ? (
          <>
            <span className={classes.dateDay}>{date.getDate()}</span>
            <span className={classes.dateMonth}>{date.toLocaleDateString(locale(i18n.language), { month: 'short' })}</span>
          </>
        ) : (
          <span className={classes.dateMonth}>–</span>
        )}
      </span>
      <div className={classes.historyBody}>
        <div className={classes.historyText}>
          <span className={classes.historyName}>{template.name}</span>
          <span className={classes.meta}>
            {date && (
              <span className="ds-sr-only">
                {t('customSurveys.dashboard.history.lastDeadline', {
                  date: date.toLocaleDateString(locale(i18n.language), { day: 'numeric', month: 'long' }),
                })}{' '}
              </span>
            )}
            {latest &&
              `${t('customSurveys.dashboard.history.responded', {
                percent,
                count: latest.responseCount,
                total: latest.expectedCount,
              })} · `}
            {t('customSurveys.dashboard.surveys', { count: template.surveysCreated ?? 0 })}
            {template.category && ` · ${template.category}`}
          </span>
        </div>
        <div className={classes.historyLinks}>
          <Link
            to={buildRoute(Routes.DashboardCustomSurveyTemplateTrends, { templateId: template.id })}
            className="ds-link"
            aria-label={t('customSurveys.dashboard.trendsFor', { name: template.name })}
          >
            {t('customSurveys.dashboard.trends')}
          </Link>
          {latest && (
            <Link
              to={buildRoute(Routes.DashboardCustomSurveySurvey, { templateId: template.id, surveyId: latest.id })}
              className="ds-link"
              aria-label={t('customSurveys.dashboard.history.latestFor', { name: template.name })}
            >
              {t('customSurveys.dashboard.history.latest')}
            </Link>
          )}
        </div>
      </div>
    </li>
  );
};

/** Historikken per måned, med søk og kategori. Radene kommer fra historyItems. */
export const HistoryList = ({ items }: { items: TemplateItem[] }) => {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const categories = categoriesOf(items);
  const months = historyByMonth(items, query, category);

  const monthLabel = (key: string | null) => {
    if (!key) return t('customSurveys.dashboard.history.noDate');
    const [year, month] = key.split('-').map(Number);
    const label = new Date(year, month - 1, 1).toLocaleDateString(locale(i18n.language), { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  return (
    <section aria-labelledby="custom-surveys-history" className={`${classes.section} ${classes.history}`}>
      <div className={classes.historyHeader}>
        <div className={classes.intro}>
          <Heading level={3} data-size="xs" id="custom-surveys-history">
            {t('customSurveys.dashboard.history.title')}
          </Heading>
          <Paragraph className={classes.subtle}>
            {t('customSurveys.dashboard.history.description')}
          </Paragraph>
        </div>
        {items.length > 0 && (
          <div role="search" className={classes.filters}>
            <Field className={classes.searchField}>
              <Label htmlFor="custom-surveys-search" data-size="sm">
                {t('customSurveys.dashboard.history.search')}
              </Label>
              <Input
                id="custom-surveys-search"
                type="search"
                data-size="sm"
                value={query}
                autoComplete="off"
                onChange={(event) => setQuery(event.target.value)}
              />
            </Field>
            {categories.length > 0 && (
              <Field className={classes.categoryField}>
                <Label htmlFor="custom-surveys-category" data-size="sm">
                  {t('customSurveys.dashboard.history.category')}
                </Label>
                <Select
                  id="custom-surveys-category"
                  data-size="sm"
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                >
                  <Select.Option value="">{t('customSurveys.dashboard.history.allCategories')}</Select.Option>
                  {categories.map((value) => (
                    <Select.Option key={value} value={value}>
                      {value}
                    </Select.Option>
                  ))}
                </Select>
              </Field>
            )}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <Paragraph data-size="sm" className={classes.subtle}>
          {t('customSurveys.dashboard.history.none')}
        </Paragraph>
      ) : months.length === 0 ? (
        <div className={classes.noMatch}>
          <Paragraph className={classes.noMatchTitle}>{t('customSurveys.dashboard.history.noMatch')}</Paragraph>
          <Button
            variant="secondary"
            data-size="sm"
            onClick={() => {
              setQuery('');
              setCategory('');
            }}
          >
            {t('customSurveys.dashboard.history.reset')}
          </Button>
        </div>
      ) : (
        <ol className={classes.months}>
          {months.map((month) => (
            <li key={month.key ?? 'none'} className={classes.month}>
              <Heading level={4} data-size="2xs" className={classes.monthLabel}>
                {monthLabel(month.key)}
              </Heading>
              <ul className={classes.historyList}>
                {month.items.map((item) => (
                  <HistoryRow key={item.template.id} item={item} />
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
};
